import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { CompanyRegistration } from '../types';

interface User {
  id: string;
  razonSocial: string;
  nombreFantasia: string;
  email: string;
  status: 'active' | 'pending_verification' | 'suspended';
  planActual: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  register: (data: CompanyRegistration) => Promise<{ success: boolean; message?: string }>;
  updateProfile: (data: Partial<CompanyRegistration>) => Promise<{ success: boolean; message?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  // Cargar datos de autenticación desde localStorage al inicializar
  useEffect(() => {
    const loadAuthData = () => {
      try {
        const storedToken = localStorage.getItem('buenaire_token');
        const storedUser = localStorage.getItem('buenaire_user');
        
        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        }
      } catch (error) {
        console.error('Error loading auth data:', error);
        // Limpiar datos corruptos
        localStorage.removeItem('buenaire_token');
        localStorage.removeItem('buenaire_user');
      } finally {
        setIsLoading(false);
      }
    };

    loadAuthData();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string }> => {
    setIsLoading(true);
    
    try {
      const response = await fetch('https://buenairebackend.carlos-mdtz9.workers.dev/api/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        const { token, user: userData } = data.data;
        
        // Mapear la respuesta del backend a nuestro formato
        const user: User = {
          id: userData.id,
          razonSocial: userData.firstName + ' ' + userData.lastName, // Temporal
          nombreFantasia: userData.firstName + ' ' + userData.lastName, // Temporal
          email: userData.email,
          status: 'active',
          planActual: 'free'
        };
        
        // Guardar en localStorage
        localStorage.setItem('buenaire_token', token);
        localStorage.setItem('buenaire_user', JSON.stringify(user));
        
        setToken(token);
        setUser(user);
        
        return { success: true, message: 'Login exitoso' };
      } else {
        // Manejar diferentes tipos de errores del backend
        let errorMessage = 'Error al iniciar sesión';
        
        if (response.status === 401) {
          errorMessage = 'Email o contraseña incorrectos. Verifica tus datos e intenta nuevamente.';
        } else if (response.status === 404) {
          errorMessage = 'USER_NOT_FOUND'; // Código especial para manejar en el componente
        } else if (response.status === 429) {
          errorMessage = 'Demasiados intentos. Espera unos minutos antes de intentar nuevamente.';
        } else if (response.status >= 500) {
          errorMessage = 'Problema del servidor. Intenta nuevamente en unos minutos.';
        } else if (data.message) {
          // Usar el mensaje del backend si está disponible
          errorMessage = data.message;
        }
        
        return { success: false, message: errorMessage };
      }
      
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'No se pudo conectar con el servidor. Verifica tu conexión a internet e intenta nuevamente.' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Limpiar localStorage
    localStorage.removeItem('buenaire_token');
    localStorage.removeItem('buenaire_user');
    
    // Limpiar estado
    setToken(null);
    setUser(null);
  };

  const register = async (data: any): Promise<{ success: boolean; message?: string }> => {
    setIsLoading(true);
    
    try {
      // Preparar datos para el endpoint de registro
      const registrationData = {
        email: data.email,
        password: data.password,
        firstName: data.firstName || 'Usuario',
        lastName: data.lastName || 'Empresa'
      };
      
      console.log('Enviando datos de registro:', registrationData);
      
      const response = await fetch('https://buenairebackend.carlos-mdtz9.workers.dev/api/v1/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationData)
      });
      
      console.log('Respuesta del servidor:', response.status, response.statusText);
      
      const responseData = await response.json();
      console.log('Datos de respuesta:', responseData);
      
      if (response.ok && responseData.success) {
        const { token, user: userData } = responseData.data;
        
        // Mapear la respuesta del backend a nuestro formato
        const user: User = {
          id: userData.id,
          razonSocial: userData.firstName + ' ' + userData.lastName,
          nombreFantasia: userData.firstName + ' ' + userData.lastName,
          email: userData.email,
          status: 'pending_verification',
          planActual: 'free'
        };
        
        // Guardar en localStorage
        localStorage.setItem('buenaire_token', token);
        localStorage.setItem('buenaire_user', JSON.stringify(user));
        
        setToken(token);
        setUser(user);
        
        return { success: true, message: 'Empresa registrada exitosamente' };
      } else {
        return { success: false, message: responseData.message || 'Error al registrar la empresa' };
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: 'Error de conexión. Inténtalo de nuevo.' };
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (data: Partial<CompanyRegistration>): Promise<{ success: boolean; message?: string }> => {
    if (!user || !token) {
      return { success: false, message: 'No autenticado' };
    }
    
    setIsLoading(true);
    
    try {
      // TODO: Reemplazar con llamada real al backend
      // const response = await fetch('/api/companies/profile', {
      //   method: 'PUT',
      //   headers: { 
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${token}`
      //   },
      //   body: JSON.stringify(data)
      // });
      
      // Simulación de actualización para desarrollo
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Actualizar usuario local
      const updatedUser = { ...user, ...data };
      localStorage.setItem('buenaire_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true, message: 'Perfil actualizado exitosamente' };
      
    } catch (error) {
      console.error('Update profile error:', error);
      return { success: false, message: 'Error al actualizar el perfil' };
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    register,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
